import { type FC, type ReactNode, useEffect } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { isErrorBoundarySuppressedError } from '@/api/apiUtil';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';
const Fallback: FC<FallbackProps> = ({
  error,
  resetErrorBoundary
}) => {
  const {
    reset
  } = useQueryErrorResetBoundary();
  if (isErrorBoundarySuppressedError(error)) {
    resetErrorBoundary();
    reset();
    return;
  }
  return <ErrorBoundaryFallback className="fixed top-1/2 -translate-y-1/2" error={error} data-sentry-element="ErrorBoundaryFallback" data-sentry-component="Fallback" data-sentry-source-file="GlobalErrorBoundary.tsx" />;
};

/**
 * Sentry 캡처는 `ErrorBoundaryFallback` 이 소유한다(`globalErrorBoundaryRendered` 태그 포함).
 *
 * 여기서 `onError` 로 또 캡처하면 안 된다 — `componentDidCatch`(커밋 단계)가 폴백의
 * `useEffect`(커밋 이후)보다 먼저 돌아, 태그 없는 이벤트가 Sentry 에 먼저 도착한다.
 * Dedupe 가 뒤이은 태그 이벤트를 버리므로 알림 룰의 태그 필터에서 통째로 누락된다.
 * (2026-01 ~ 2026-09 실제로 이 상태였다)
 */
const GlobalErrorBoundary = ({
  children
}: Props) => {
  return <ErrorBoundary FallbackComponent={Fallback} data-sentry-element="ErrorBoundary" data-sentry-component="GlobalErrorBoundary" data-sentry-source-file="GlobalErrorBoundary.tsx">{children}</ErrorBoundary>;
};
export default GlobalErrorBoundary;
type Props = {
  children: ReactNode;
};
export const useUnhandledRejectionErrors = (handler: Handler) => {
  useEffect(() => {
    window.addEventListener('unhandledrejection', handler);
    return () => {
      window.removeEventListener('unhandledrejection', handler);
    };
  }, [handler]);
};
type Handler = (event: PromiseRejectionEvent) => void;